import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  CheckCircle,
  XCircle,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  ChevronRight,
  Search,
  Wind, 
  DoorOpen
} from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";

const DeviceManagement = () => {
  const [devices, setDevices] = useState([]);
  const { logout } = useAuth0();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [idleThreshold, setIdleThreshold] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const CATEGORY_TRANSLATIONS = {
    "Occupancy Sensors": "占有センサー",
    "IAQ Sensors": "IAQセンサー", 
    "MDR Sensors": "MDRセンサー",
    "Water Leakage Sensors": "水漏れセンサー",
    "Footfall Sensors": "フットフォールセンサー"
  };

  const getJapaneseDisplayName = (englishCategory) => {
    return CATEGORY_TRANSLATIONS[englishCategory] || englishCategory;
  };

  // Devices to be excluded from display
  const EXCLUDED_DEVICE_IDS = [
    "AM103-915M-01iaq_sensor",
    "AM103-915M-01",
    "WS301-915M-01",
  ];

  // Dummy data generators
  const generateDummyDevices = () => {
    const now = new Date();
    
    // Occupancy Sensors - Exact list of 30
    const occupancyDeviceIds = [
      "1F-CR1-V01", "1F-CR1-V02", "1F-CR1-V03", "1F-CR1-V04", 
      "1F-CR2-V01", "1F-CR2-V02", "1F-CR2-V03", "1F-CR2-V04",
      "1F-LRR-V01", "1F-LRR-V02", "1F-LRR-V03", "1F-LRR-V04", "1F-LRR-V05",
      "1F-NC-V01", "1F-SC-V01",
      "3F-SC-V01", "3F-SC-V02", "3F-SC-V03", "3F-SC-V04", "3F-SC-V05",
      "DS-01", "DS-02", "DS-03", "DS-04", "DS-05", "DS-06", "DS-07", 
      "MF-SC-V01", "MF-SC-V02", "MF-SC-V03"
    ];
    
    // Footfall Sensors - Exact list of 21
    const footfallDeviceIds = [
      "1F-LRR-EX01", "1F-LRR-EX02", 
      "1F-L-B01", "1F-ME-B01", "1F-Lobby-V01", "MF-S-B01", 
      "1F-BF-B01", "1F-BF-B02", "1F-Lobby-V03", "MF-S-V03", 
      "MF-S-V01", "MF-S-V02", 
      "2F-S-V03", "2F-L-B01", "2F-S-V02", "2F-S-V04", "2F-S-V01", 
      "3F-S-B01", "3F-L-V01", "3F-S-B02", 
      "1F-ME-B02"
    ];

    // IAQ Sensors
    const iaqDeviceIds = ["IAQ-P01", "IAQ-P02", "IAQ-P03", "IAQ-P04", "IAQ-P05", "IAQ-L01", "IAQ-L02", "IAQ-L03", "IAQ-L04", "IAQ-L05"];
    
    // Water Leakage Sensors
    const wlDeviceIds = ["WL-01", "WL-02", "WL-03", "WL-04", "WL-05", "WL-06", "WL-07", "WL-08"];
    
    // MDR Sensors
    const mdrDeviceIds = ["MDR-01", "MDR-02", "MDR-03", "MDR-04", "MDR-05"];

    const devices = [];

    // Generate Occupancy Sensors
    occupancyDeviceIds.forEach((id, index) => {
      const hoursAgo = Math.random() < 0.85 ? Math.random() * 2 : Math.random() * 72 + 24; // Most recent, some old
      const lastUpdate = new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000));
      
      devices.push({
        device_id: id,
        device_type: "occupancy-sensors",
        location: `フロア ${id.charAt(0)}, エリア ${id.split('-')[1]}`,
        area: `ゾーン-${id.split('-')[1]}`,
        last_data_read: lastUpdate.toISOString(),
        battery: null, // POE powered
        isPoweredByPOE: true
      });
    });

    // Generate Footfall Sensors
    footfallDeviceIds.forEach((id, index) => {
      const hoursAgo = Math.random() < 0.9 ? Math.random() * 1 : Math.random() * 48 + 12;
      const lastUpdate = new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000));
      const isBattery = id.includes("-B"); // Battery powered if contains -B
      
      devices.push({
        device_id: id,
        device_type: "Footfall Sensor",
        location: `フロア ${id.charAt(0)}, ${id.includes('Lobby') ? 'ロビー' : '学習エリア'}`,
        area: id.split('-')[1] || 'メイン',
        last_data_read: lastUpdate.toISOString(),
        battery: isBattery ? Math.round(70 + Math.random() * 30) : null,
        isPoweredByPOE: !isBattery
      });
    });

    // Generate IAQ Sensors
    iaqDeviceIds.forEach((id, index) => {
      const hoursAgo = Math.random() < 0.8 ? Math.random() * 0.5 : Math.random() * 24 + 2;
      const lastUpdate = new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000));
      
      devices.push({
        device_id: id,
        device_type: id.startsWith("IAQ-P") ? "IAQ-P" : "IAQ-L",
        location: `フロア ${index % 3 + 1}, ${id.startsWith("IAQ-P") ? '学習エリア' : '読書室'} ${id.slice(-1)}`,
        area: `エリア-${100 + index}`,
        last_data_read: lastUpdate.toISOString(),
        battery: Math.round(60 + Math.random() * 40),
        isPoweredByPOE: false
      });
    });

    // Generate Water Leakage Sensors
    wlDeviceIds.forEach((id, index) => {
      const hoursAgo = ["WL-05", "WL-06", "WL-07"].includes(id) ? null : Math.random() < 0.9 ? Math.random() * 2 : Math.random() * 48 + 6;
      const lastUpdate = hoursAgo ? new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000)) : null;
      
      devices.push({
        device_id: id,
        device_type: "water-leakage",
        location: `フロア ${index % 3 + 1}, ${index % 2 === 0 ? 'ユーティリティ室' : '貯蔵エリア'} ${id.slice(-1)}`,
        area: `WL-ゾーン-${id.slice(-1)}`,
        last_data_read: lastUpdate ? lastUpdate.toISOString() : null,
        battery: Math.round(65 + Math.random() * 35),
        isPoweredByPOE: false
      });
    });

    // Generate MDR Sensors
    mdrDeviceIds.forEach((id, index) => {
      const hoursAgo = Math.random() < 0.85 ? Math.random() * 1 : Math.random() * 36 + 12;
      const lastUpdate = new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000));
      
      devices.push({
        device_id: id,
        device_type: "magnetic-door",
        location: `フロア ${index % 2 + 1}, ドア ${id.slice(-1)}`,
        area: `MDR-ゾーン-${id.slice(-1)}`,
        last_data_read: lastUpdate.toISOString(),
        battery: Math.round(70 + Math.random() * 30),
        isPoweredByPOE: false
      });
    });

    return devices;
  };

  const generateDummyBatteryLevels = () => {
    const batteries = {};
    
    // Generate battery levels for battery-powered devices
    const batteryDevices = [
      "IAQ-P01", "IAQ-P02", "IAQ-P03", "IAQ-P04", "IAQ-P05",
      "IAQ-L01", "IAQ-L02", "IAQ-L03", "IAQ-L04", "IAQ-L05",
      "WL-01", "WL-02", "WL-03", "WL-04", "WL-08",
      "MDR-01", "MDR-02", "MDR-03", "MDR-04", "MDR-05",
      "1F-L-B01", "1F-ME-B01", "MF-S-B01", "1F-BF-B01", "1F-BF-B02", "2F-L-B01", "3F-S-B01", "3F-S-B02", "1F-ME-B02"
    ];

    batteryDevices.forEach(deviceId => {
      batteries[deviceId] = {
        battery: Math.round(60 + Math.random() * 40)
      };
    });

    return batteries;
  };

  const generateDummySensorData = () => {
    const data = {
      iaq: {},
      wl: {},
      mdr: {},
      footfall: {},
      occupancy: {}
    };

    // IAQ Sensor Data
    const iaqDevices = ["IAQ-P01", "IAQ-P02", "IAQ-P03", "IAQ-P04", "IAQ-P05", "IAQ-L01", "IAQ-L02", "IAQ-L03", "IAQ-L04", "IAQ-L05"];
    iaqDevices.forEach(id => {
      const isP_Sensor = id.startsWith("IAQ-P");
      data.iaq[id] = {
        temperature: (20 + Math.random() * 8).toFixed(1),
        humidity: (45 + Math.random() * 30).toFixed(1),
        co2: Math.round(400 + Math.random() * 800),
        pm2_5: isP_Sensor ? (5 + Math.random() * 35).toFixed(1) : undefined,
        pm10: isP_Sensor ? (10 + Math.random() * 40).toFixed(1) : undefined,
        tvoc: isP_Sensor ? (50 + Math.random() * 400).toFixed(1) : undefined,
        pressure: (1010 + Math.random() * 20).toFixed(1),
        light_level: Math.round(Math.random() * 1000),
        battery: Math.round(70 + Math.random() * 30),
        timestamp: new Date().toISOString()
      };
    });

    // Water Leakage Sensor Data
    const wlDevices = ["WL-01", "WL-02", "WL-03", "WL-04", "WL-08"];
    wlDevices.forEach(id => {
      data.wl[id] = {
        leakage_status: Math.random() > 0.1 ? "normal" : "leak_detected",
        battery: Math.round(65 + Math.random() * 35),
        timestamp: new Date().toISOString()
      };
    });

    // MDR Sensor Data
    const mdrDevices = ["MDR-01", "MDR-02", "MDR-03", "MDR-04", "MDR-05"];
    mdrDevices.forEach(id => {
      data.mdr[id] = {
        magnet_status: Math.random() > 0.2 ? "close" : "open",
        tamper_status: Math.random() > 0.05 ? "installed" : "tampered",
        battery: Math.round(70 + Math.random() * 30),
        timestamp: new Date().toISOString()
      };
    });

    // Footfall Sensor Data
    const footfallDevices = [
      "1F-LRR-EX01", "1F-LRR-EX02", "1F-L-B01", "1F-ME-B01", "1F-Lobby-V01", "MF-S-B01", 
      "1F-BF-B01", "1F-BF-B02", "1F-Lobby-V03", "MF-S-V03", "MF-S-V01", "MF-S-V02", 
      "2F-S-V03", "2F-L-B01", "2F-S-V02", "2F-S-V04", "2F-S-V01", 
      "3F-S-B01", "3F-L-V01", "3F-S-B02", "1F-ME-B02"
    ];
    
    footfallDevices.forEach(id => {
      const maxCapacity = Math.round(20 + Math.random() * 80);
      const currentCount = Math.round(Math.random() * maxCapacity * 0.8);
      
      data.footfall[id] = {
        count: currentCount,
        maxCapacity: maxCapacity,
        zone: id.includes('Lobby') ? 'ロビー' : '学習ゾーン',
        newZone: id.includes('Lobby') ? 'メインロビー' : `学習エリア ${id.slice(-1)}`,
        floor: id.charAt(0) + 'F',
        building: 'LNU',
        status: currentCount < maxCapacity ? "normal" : "overcrowded",
        timestamp: new Date().toISOString(),
        battery: id.includes("-B") ? Math.round(70 + Math.random() * 30) : 100
      };
    });

    // Occupancy Sensor Data
    const occupancyDevices = [
      "1F-CR1-V01", "1F-CR1-V02", "1F-CR1-V03", "1F-CR1-V04", 
      "1F-CR2-V01", "1F-CR2-V02", "1F-CR2-V03", "1F-CR2-V04",
      "1F-LRR-V01", "1F-LRR-V02", "1F-LRR-V03", "1F-LRR-V04", "1F-LRR-V05",
      "1F-NC-V01", "1F-SC-V01", "3F-SC-V01", "3F-SC-V02", "3F-SC-V03", "3F-SC-V04", "3F-SC-V05",
      "DS-01", "DS-02", "DS-03", "DS-04", "DS-05", "DS-06", "DS-07", 
      "MF-SC-V01", "MF-SC-V02", "MF-SC-V03"
    ];
    
    occupancyDevices.forEach(id => {
      data.occupancy[id] = {
        status: Math.random() > 0.1 ? "active" : "inactive",
        location: `フロア ${id.charAt(0)}, ルーム ${id.split('-')[1]}`,
        area: `ゾーン-${id.split('-')[1]}`,
        battery: 100, // POE powered
        timestamp: new Date().toISOString(),
        isPoweredByPOE: true
      };
    });

    return data;
  };

  // Function to determine device type for sorting order and categorization
  const getDeviceType = (deviceId, devType) => {
    // OCCUPANCY SENSORS - Exact list of 30
    const occupancyDeviceIds = [
      "1F-CR1-V01", "1F-CR1-V02", "1F-CR1-V03", "1F-CR1-V04", 
      "1F-CR2-V01", "1F-CR2-V02", "1F-CR2-V03", "1F-CR2-V04",
      "1F-LRR-V01", "1F-LRR-V02", "1F-LRR-V03", "1F-LRR-V04", "1F-LRR-V05",
      "1F-NC-V01", "1F-SC-V01",
      "3F-SC-V01", "3F-SC-V02", "3F-SC-V03", "3F-SC-V04", "3F-SC-V05",
      "DS-01", "DS-02", "DS-03", "DS-04", "DS-05", "DS-06", "DS-07", 
      "MF-SC-V01", "MF-SC-V02", "MF-SC-V03"
    ];
    
    // FOOTFALL SENSORS - Exact list of the 21 unique IDs
    const footfallDeviceIds = [
      "1F-LRR-EX01", "1F-LRR-EX02", 
      "1F-L-B01", "1F-ME-B01", "1F-Lobby-V01", "MF-S-B01", 
      "1F-BF-B01", "1F-BF-B02", "1F-Lobby-V03", "MF-S-V03", 
      "MF-S-V01", "MF-S-V02", 
      "2F-S-V03", "2F-L-B01", "2F-S-V02", "2F-S-V04", "2F-S-V01", 
      "3F-S-B01", "3F-L-V01", "3F-S-B02", 
      "1F-ME-B02"
    ];

    // Check occupancy first
    if (occupancyDeviceIds.includes(deviceId)) {
      return "Occupancy Sensors";
    }
    
    // Then check footfall
    if (footfallDeviceIds.includes(deviceId)) {
      return "Footfall Sensors";
    }
    
    // IAQ Sensors
    if (deviceId.toLowerCase().includes("iaq")) {
      return "IAQ Sensors";
    }
    
    // Water Leakage Sensors
    if (deviceId.toLowerCase().startsWith("wl")) {
      return "Water Leakage Sensors";
    }
    
    // MDR Sensors
    if (deviceId.startsWith("MDR-")) {
      return "MDR Sensors";
    }
    
    // If none of the specific patterns match, return null
    return null;
  };

  // Function to compare devices for sorting
  const compareDevices = (a, b) => {
    // Extract numbers from device IDs
    const getNumericPart = (id) => {
      const matches = id.match(/\d+/);
      return matches ? parseInt(matches[0], 10) : 0;
    };

    return getNumericPart(a.id) - getNumericPart(b.id);
  };

  // Simulate API calls with dummy data
  const fetchDevices = async () => {
    try {
      setLoading(true);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log("ダミーデバイスデータを読み込んでいます...");
      
      // Generate dummy data
      const dummyDevices = generateDummyDevices();
      const batteryLevels = generateDummyBatteryLevels();
      const sensorData = generateDummySensorData();

      console.log("生成されたデバイス:", dummyDevices.length);
      console.log("生成されたセンサーデータ:", sensorData);

      // Transform data to match component needs
      const transformedData = dummyDevices
        .filter((device) => !EXCLUDED_DEVICE_IDS.includes(device.device_id))
        .map((device) => {
          const deviceId = device.device_id || "";
          const deviceType = getDeviceType(deviceId, device.device_type);
          
          // Skip devices that don't match any of our specific categories
          if (!deviceType) return null;

          let sensorInfo = null;
          let lastUpdatedTime = new Date(device.last_data_read || new Date());

          // Determine if device is POE powered or battery powered
          const hasBatteryLevel = batteryLevels[deviceId];
          const actualBatteryLevel = hasBatteryLevel ? batteryLevels[deviceId].battery : null;

          // Update isPoweredByPOE logic
          const isPoweredByPOE = !hasBatteryLevel && (
            deviceType === "Occupancy Sensors" || 
            (deviceType === "Footfall Sensors" && !deviceId.includes("-B"))
          );

          // Get sensor-specific data
          if (deviceType === "IAQ Sensors" && sensorData.iaq[deviceId]) {
            sensorInfo = sensorData.iaq[deviceId];
            lastUpdatedTime = new Date(sensorInfo.timestamp);
          } else if (deviceType === "Water Leakage Sensors") {
            // Special handling for WL-05, 06, 07
            if (["WL-05", "WL-06", "WL-07"].includes(deviceId)) {
              sensorInfo = { lastUpdated: "N/A" };
            } else if (sensorData.wl[deviceId]) {
              sensorInfo = sensorData.wl[deviceId];
              lastUpdatedTime = new Date(sensorInfo.timestamp);
            }
          } else if (deviceType === "MDR Sensors" && sensorData.mdr[deviceId]) {
            sensorInfo = sensorData.mdr[deviceId];
            lastUpdatedTime = new Date(sensorInfo.timestamp);
          } else if (deviceType === "Footfall Sensors" && sensorData.footfall[deviceId]) {
            sensorInfo = sensorData.footfall[deviceId];
            lastUpdatedTime = new Date(sensorInfo.timestamp);
          } else if (deviceType === "Occupancy Sensors" && sensorData.occupancy[deviceId]) {
            sensorInfo = sensorData.occupancy[deviceId];
            lastUpdatedTime = new Date(sensorInfo.timestamp || device.last_data_read);
          }

          return {
            id: deviceId,
            type: device.device_type || "",
            category: deviceType,
            location: device.location || "",
            area: device.area || "",
            lastUpdated:
              sensorInfo && sensorInfo.lastUpdated === "N/A"
                ? "N/A"
                : new Date(lastUpdatedTime).toLocaleString(),
            battery: actualBatteryLevel !== null 
              ? actualBatteryLevel 
              : (sensorInfo ? sensorInfo.battery || device.battery || 100 : device.battery || 100),
            lastActive:
              sensorInfo && sensorInfo.lastUpdated === "N/A"
                ? null
                : lastUpdatedTime,
            sensorData: sensorInfo || null,
            isPoweredByPOE: isPoweredByPOE
          };
        })
        .filter(device => device !== null)
        .sort(compareDevices);

      setDevices(transformedData);

      // Set initial active tab if there are devices
      if (transformedData.length > 0) {
        const categories = [
          ...new Set(transformedData.map((device) => device.category)),
        ];
        if (!activeTab && categories.length > 0) {
          setActiveTab(categories[0]);
        }
      }

      setError(null);
      console.log("正常に読み込まれました", transformedData.length, "デバイス");
    } catch (err) {
      console.error("デバイス生成エラー:", err);
      setError("デバイスの読み込みに失敗しました。再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  // Simulate device update
  const updateDevice = async (deviceId, updatedData) => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log("デバイス更新をシミュレート中:", {
        deviceId,
        updatedData: {
          device_type: updatedData.type,
          location: updatedData.location,
          area: updatedData.area,
        }
      });

      // Update local state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.id === deviceId 
            ? { ...device, type: updatedData.type, location: updatedData.location, area: updatedData.area }
            : device
        )
      );

      // Show success message
      setSuccessMessage(`デバイス ${deviceId} が正常に更新されました！`);
      setTimeout(() => setSuccessMessage(""), 3000);

      return true;
    } catch (err) {
      console.error("デバイス更新エラー:", err);
      return false;
    }
  };

  // Simulate idle threshold update
  const updateIdleThreshold = async (days) => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log("アイドルしきい値更新をシミュレート中:", { thresholdDays: days });
      
      // Show success message
      setSuccessMessage(`アイドルしきい値が ${days} 日に更新されました！`);
      setTimeout(() => setSuccessMessage(""), 3000);

      return true;
    } catch (err) {
      console.error("アイドルしきい値更新エラー:", err);
      return false;
    }
  };

  // Load devices on component mount
  useEffect(() => {
    fetchDevices();
  }, []);

  // Auto-hide success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle idle threshold change
  const handleThresholdChange = async (newThreshold) => {
    const success = await updateIdleThreshold(Number(newThreshold));

    if (success) {
      setIdleThreshold(Number(newThreshold));
    } else {
      alert("アイドルしきい値の更新に失敗しました。再度お試しください。");
    }
  };

  const handleEdit = (device) => {
    setEditingDevice({ ...device });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    const success = await updateDevice(editingDevice.id, editingDevice);

    if (success) {
      setIsEditModalOpen(false);
    } else {
      alert("デバイスの更新に失敗しました。再度お試しください。");
    }
  };

  // Function to check if a device is idle based on lastActive date
  const isIdle = (lastActiveDate) => {
    if (!lastActiveDate) return false; // For N/A cases

    const today = new Date();
    const diffTime = Math.abs(today - lastActiveDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > idleThreshold;
  };

  // Helper function to get the appropriate battery icon
  const getBatteryIcon = (batteryLevel) => {
    if (batteryLevel > 70) {
      return <BatteryFull className="text-green-500 w-5 h-5" />;
    } else if (batteryLevel >= 40) {
      return <BatteryMedium className="text-yellow-500 w-5 h-5" />;
    } else {
      return <BatteryLow className="text-red-500 w-5 h-5" />;
    }
  };

  // Search devices by ID or type - Fixed to handle null/undefined values
  const filteredDevices = devices.filter((device) => {
    if (!searchTerm) return true;

    const lowercaseSearch = searchTerm.toLowerCase();
    const deviceId = (device.id || "").toLowerCase();
    const deviceType = (device.type || "").toLowerCase();
    const deviceLocation = (device.location || "").toLowerCase();
    const deviceArea = (device.area || "").toLowerCase();

    return (
      deviceId.includes(lowercaseSearch) ||
      deviceType.includes(lowercaseSearch) ||
      deviceLocation.includes(lowercaseSearch) ||
      deviceArea.includes(lowercaseSearch)
    );
  });

  // Get unique device categories from filtered devices
  const deviceCategories = [
    ...new Set(filteredDevices.map((device) => device.category)),
  ];

  // Device count by category for filtered devices
  const getCategoryCount = (category) => {
    return filteredDevices.filter((device) => device.category === category)
      .length;
  };

  // Toggle device details
  const toggleDeviceDetails = (deviceId) => {
    if (expandedDevice === deviceId) {
      setExpandedDevice(null);
    } else {
      setExpandedDevice(deviceId);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Set active tab to the first category with results when searching
    if (e.target.value) {
      const filtered = devices.filter((device) => {
        const lowercaseSearch = e.target.value.toLowerCase();
        const deviceId = (device.id || "").toLowerCase();
        const deviceType = (device.type || "").toLowerCase();

        return (
          deviceId.includes(lowercaseSearch) ||
          deviceType.includes(lowercaseSearch)
        );
      });

      const categories = [
        ...new Set(filtered.map((device) => device.category)),
      ];
      if (categories.length > 0) {
        setActiveTab(categories[0]);
      }
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="p-6 md:mt-16 md:mx-5 lg:mt-24 lg:mx-6 mt-12 mx-2">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        logout={logout}
      />

      {/* Header */}
      <Header
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        showWeatherData={true}  
        showLiveCount={true}    
      />

      <div className="flex justify-between items-center md:mb-6 mb-4">
        <h1 className="md:text-2xl text-xl font-bold">デバイス​</h1>

        {/* Idle threshold dropdown */}
        <div className="flex items-center space-x-2">
          <label
            htmlFor="idleThreshold"
            className="text-gray-600 font-medium text-sm md:text-lg"
          >
            アイドルしきい値:
          </label>
          <select
            id="idleThreshold"
            value={idleThreshold}
            onChange={(e) => handleThresholdChange(e.target.value)}
            className="sm:p-2 p-1 border rounded-md md:text-sm text-xs focus:ring-2 focus:ring-blue-500"
          >
            <option value="1">1 day</option>
            <option value="2">2 days</option>
            <option value="3">3 days</option>
            <option value="5">5 days</option>
            <option value="7">7 days</option>
          </select>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border-l-4 border-green-500 text-green-700 rounded">
          <p>{successMessage}</p>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative mb-6">
        <div className="flex items-center border rounded-md bg-white shadow-sm">
          <div className="px-3 py-3">
            <Search className="w-5 h-5 text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="デバイスID・種類・場所・エリアのいずれかで検索"
            value={searchTerm}
            onChange={handleSearchChange}
            className="flex-1 p-2 outline-none"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="px-3 py-2 text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span>デバイスデータを読み込み中...</span>
          </div>
        </div>
      )}
      {error && <div className="text-center py-8 text-red-500">{error}</div>}

      {/* No Results Message */}
      {!loading && !error && filteredDevices.length === 0 && searchTerm && (
        <div className="text-center py-8 text-gray-500">
          "{searchTerm}"に一致するデバイスが見つかりません
        </div>
      )}

      {/* Category Tabs */}
      {!loading && !error && filteredDevices.length > 0 && (
        <div className="mb-6 space-y-4">
          {deviceCategories.map((category) => (
            <div
              key={category}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() =>
                  setActiveTab(activeTab === category ? null : category)
                }
              >
                <div className="flex items-center">
                  <div className="bg-gray-100 rounded-full p-3 mr-4">
                    {category === "IAQ Sensors" && (
                      <span className="text-xl">
                        <Wind size={20} />
                      </span>
                    )}
                    {category === "Water Leakage Sensors" && (
                      <span className="text-xl">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 2C12 2 6 10 6 14a6 6 0 0012 0c0-4-6-12-6-12z" />
                        </svg>
                      </span>
                    )}
                    {category === "MDR Sensors" && (
                      <span className="text-xl">
                        <DoorOpen size={20} />
                      </span>
                    )}
                    {category === "Footfall Sensors" && (
                      <span className="text-xl">
                        <img
                          width="20"
                          height="20"
                          src="https://img.icons8.com/ios/50/walking.png"
                          alt="footfall-sensor"
                        />
                      </span>
                    )}
                    {category === "Occupancy Sensors" && (
                      <span className="text-xl">
                        <img
                          width="20"
                          height="20"
                          src="https://img.icons8.com/plumpy/24/sensor.png"
                          alt="sensor"
                        />
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">{getJapaneseDisplayName(category)}</h2>
                    <p className="text-sm text-gray-500">
                      {getCategoryCount(category)} devices
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <ChevronRight
                    className={`w-6 h-6 transform transition-transform ${
                      activeTab === category ? "rotate-90" : ""
                    }`}
                  />
                </div>
              </div>

              {/* Devices List only shown when tab is active */}
              {activeTab === category && (
                <div className="border-t">
                  {filteredDevices
                    .filter((device) => device.category === category)
                    .sort((a, b) => {
                      // Special sorting for Occupancy Sensors category
                      if (category === "Occupancy Sensors") {
                        // First sort by idle status (idle devices first)
                        const aIsIdle = isIdle(a.lastActive);
                        const bIsIdle = isIdle(b.lastActive);

                        if (aIsIdle && !bIsIdle) return -1;
                        if (!aIsIdle && bIsIdle) return 1;

                        // Then sort by device ID
                        return compareDevices(a, b);
                      }

                      // Default sorting for other categories
                      return compareDevices(a, b);
                    })
                    .map((device) => (
                      <div key={device.id} className="border-b last:border-b-0">
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                          onClick={() => toggleDeviceDetails(device.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-1">
                              {isIdle(device.lastActive) ? (
                                <XCircle className="text-gray-500 w-5 h-5" />
                              ) : (
                                <CheckCircle className="text-green-500 w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium sm:text-lg text-sm">
                                {device.id}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {device.location}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-8">
                            <div className="flex items-center">
                              {device.isPoweredByPOE ? (
                                <>
                                  <img width="16" height="16" src="https://img.icons8.com/forma-light/24/electrical.png" alt="electrical"/>
                                  <span className="ml-1 text-sm">POE</span>
                                </>
                              ) : (
                                <>
                                  {getBatteryIcon(device.battery)}
                                  <span className="ml-1 text-sm">
                                    {device.battery}%
                                  </span>
                                </>
                              )}
                            </div>
                            <ChevronRight
                              className={`w-5 h-5 transform transition-transform ${
                                expandedDevice === device.id ? "rotate-90" : ""
                              }`}
                            />
                          </div>
                        </div>

                        {/* Expanded Device Details */}
                        {expandedDevice === device.id && (
                          <div className="p-4 bg-gray-50 border-t">
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  デバイスタイプ
                                </p>
                                <p>{device.type}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">ステータス</p>
                                <p>
                                  {device.lastUpdated === "N/A"
                                    ? "不明"
                                    : isIdle(device.lastActive)
                                    ? "アイドル"
                                    : "アクティブ"}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  場所
                                </p>
                                <p>{device.location}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">エリア</p>
                                <p>{device.area}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">電源</p>
                                <div className="flex items-center">
                                  {device.isPoweredByPOE ? (
                                    <>
                                      <img width="16" height="16" src="https://img.icons8.com/forma-light/24/electrical.png" alt="electrical"/>
                                      <span className="ml-1">POE電源</span>
                                    </>
                                  ) : (
                                    <>
                                      {getBatteryIcon(device.battery)}
                                      <span className="ml-1">
                                        {device.battery}%
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">
                                  最終更新
                                </p>
                                <p>{device.lastUpdated}</p>
                              </div>

                              {/* Show sensor-specific data if available */}
                              {device.sensorData &&
                                device.category === "IAQ Sensors" && (
                                  <>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        温度
                                      </p>
                                      <p>{device.sensorData.temperature}°C</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        湿度
                                      </p>
                                      <p>{device.sensorData.humidity}%</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        CO2
                                      </p>
                                      <p>{device.sensorData.co2} ppm</p>
                                    </div>
                                    {device.sensorData.pm2_5 && (
                                      <div>
                                        <p className="text-sm text-gray-500">
                                          PM2.5
                                        </p>
                                        <p>{device.sensorData.pm2_5} μg/m³</p>
                                      </div>
                                    )}
                                  </>
                                )}

                              {device.sensorData &&
                                device.category === "Water Leakage Sensors" &&
                                device.lastUpdated !== "N/A" && (
                                  <div>
                                    <p className="text-sm text-gray-500">
                                      漏水ステータス
                                    </p>
                                    <p
                                      className={
                                        device.sensorData.leakage_status ===
                                        "normal"
                                          ? "text-green-500"
                                          : "text-red-500"
                                      }
                                    >
                                      {device.sensorData.leakage_status ===
                                      "normal"
                                        ? "正常"
                                        : "漏水検出"}
                                    </p>
                                  </div>
                                )}

                              {device.sensorData &&
                                device.category === "MDR Sensors" && (
                                  <>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        磁石ステータス
                                      </p>
                                      <p
                                        className={
                                          device.sensorData.magnet_status ===
                                          "close"
                                            ? "text-green-500"
                                            : "text-red-500"
                                        }
                                      >
                                        {device.sensorData.magnet_status ===
                                        "close"
                                          ? "閉"
                                          : "開"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        改ざんステータス
                                      </p>
                                      <p
                                        className={
                                          device.sensorData.tamper_status ===
                                          "installed"
                                            ? "text-green-500"
                                            : "text-red-500"
                                        }
                                      >
                                        {device.sensorData.tamper_status ===
                                        "installed"
                                          ? "設置済み"
                                          : "改ざん"}
                                      </p>
                                    </div>
                                  </>
                                )}
                                
                              {device.sensorData &&
                                device.category === "Footfall Sensors" && (
                                  <>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        現在の収容人数
                                      </p>
                                      <p>{device.sensorData.count || 0} 人</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        最大収容人数
                                      </p>
                                      <p>{device.sensorData.maxCapacity || 0} 人</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        ゾーン
                                      </p>
                                      <p>{device.sensorData.zone || 'N/A'} ({device.sensorData.newZone || 'N/A'})</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        フロア
                                      </p>
                                      <p>{device.sensorData.floor || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        ステータス
                                      </p>
                                      <p className={
                                        device.sensorData.status === "normal"
                                          ? "text-green-500"
                                          : "text-red-500"
                                      }>
                                        {device.sensorData.status === "normal" ? "正常" : "過密"}
                                      </p>
                                    </div>
                                  </>
                                )}
                                
                              {device.sensorData &&
                                device.category === "Occupancy Sensors" && (
                                  <>
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        デバイスステータス
                                      </p>
                                      <p className={
                                        device.sensorData.status === "active"
                                          ? "text-green-500"
                                          : "text-yellow-500"
                                      }>
                                        {device.sensorData.status === "active" ? "アクティブ" : device.sensorData.status}
                                      </p>
                                    </div>
                                    {device.sensorData.location && (
                                      <div>
                                        <p className="text-sm text-gray-500">
                                          場所詳細
                                        </p>
                                        <p>{device.sensorData.location}</p>
                                      </div>
                                    )}
                                    <div>
                                      <p className="text-sm text-gray-500">
                                        電源タイプ
                                      </p>
                                      <p>POE (Power Over Ethernet)</p>
                                    </div>
                                  </>
                                )}
                            </div>
                            <div className="flex justify-end">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(device);
                                }}
                                className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                編集
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">デバイス編集</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  デバイスID
                </label>
                <input
                  type="text"
                  value={editingDevice.id}
                  readOnly
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  デバイスタイプ
                </label>
                <input
                  type="text"
                  value={editingDevice.type}
                  onChange={(e) =>
                    setEditingDevice({
                      ...editingDevice,
                      type: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  場所
                </label>
                <input
                  type="text"
                  value={editingDevice.location}
                  onChange={(e) =>
                    setEditingDevice({
                      ...editingDevice,
                      location: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  エリア
                </label>
                <input
                  type="text"
                  value={editingDevice.area}
                  onChange={(e) =>
                    setEditingDevice({
                      ...editingDevice,
                      area: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  変更を保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;